import React from 'react';
import PageHero from '../components/PageHero';
import Status from '../components/Status';
import SourceLink from '../components/SourceLink';
import { SITE, SOURCES } from '../content/site';

const Scorecard: React.FC = () => (
  <div>
    <PageHero
      title="BA Forecast Scorecard"
      lead="EIA-930 publishes, for every U.S. balancing authority, the hourly demand and the day-ahead forecast the BA itself submitted, back to 2015. The scorecard scores that forecast every day: by BA, by season, on extreme-weather days, and during named events."
    >
      <div className="flex flex-wrap items-center gap-3">
        <Status tone="live">Pipeline live · v0.1.1</Status>
        <Status tone="planned">Dashboard October 2026</Status>
        <span className="text-sm text-gray-500">Code, tests, a daily GitHub Action and results for the full 2015–2026 record are public on GitHub and archived on Zenodo; the dashboard follows.</span>
      </div>
    </PageHero>

    <section className="bg-white section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">What it measures</h2>
            <ul className="space-y-3 text-gray-700">
              <Bullet>Day-ahead error by balancing authority: MAPE, RMSE, bias, and error at the daily peak hour.</Bullet>
              <Bullet>Error on extreme-weather days, defined from NOAA station data, separated from ordinary days.</Bullet>
              <Bullet>Named events: Winter Storm Uri (2021), Winter Storm Elliott (2022), the January 2024 Arctic storms, summer heat waves, and this winter's events as they happen.</Bullet>
              <Bullet>Trends 2015–2026 and a monthly ranking, with the data-quality layer shown alongside the error.</Bullet>
            </ul>
          </div>
          <div>
            <h2 className="text-3xl font-poppins font-bold text-dark-gray mb-6">Data-quality layer</h2>
            <ul className="space-y-3 text-gray-700">
              <Bullet>Missing hours and values imputed by EIA, by BA and by year.</Bullet>
              <Bullet>Implausible jumps and flat lines in demand or forecast series.</Bullet>
              <Bullet>Genuine extreme readings versus telemetry faults, using the Grid Data Sentinel.</Bullet>
              <Bullet>Every flag is explained; nothing is silently removed.</Bullet>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-gray-50 section-padding">
      <div className="container-width">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <Box title="Source data">
            Hourly demand and day-ahead demand forecast per balancing authority from EIA-930, plus ISO/RTO zonal load
            and NOAA weather. <SourceLink source={SOURCES.eia930} />
          </Box>
          <Box title="Method and code">
            The pipeline, the metric definitions, the event calendar and the first results are public in the repository, with a daily update. Each monthly report will be archived with a DOI so that a number quoted today can be checked years from now.
          </Box>
          <Box title="Cite">
            Concept DOI <a href="https://doi.org/10.5281/zenodo.22697386" target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">10.5281/zenodo.22697386</a> (always the latest version); v0.1.1: <a href="https://doi.org/10.5281/zenodo.22697387" target="_blank" rel="noopener noreferrer" className="text-primary-red hover:underline">10.5281/zenodo.22697387</a>. Citation metadata in the repository's CITATION.cff.
          </Box>
          <Box title="Known limitations">
            EIA-930 forecasts are as submitted by each BA and may be revised; imputed values are flagged, not
            corrected; weather attribution uses public stations, not each BA's internal weather zones.
          </Box>
        </div>
        <div className="text-center mt-12">
          <a href={SITE.github} target="_blank" rel="noopener noreferrer" className="btn-primary">
            Follow the build on GitHub
          </a>
        </div>
      </div>
    </section>
  </div>
);

const Bullet: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <li className="flex items-start space-x-3">
    <span className="w-6 h-6 bg-primary-red rounded-full flex items-center justify-center flex-shrink-0 mt-1">
      <span className="text-white text-xs">✓</span>
    </span>
    <span>{children}</span>
  </li>
);

const Box: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-xl p-6 shadow">
    <h3 className="font-poppins font-semibold text-dark-gray text-lg mb-3">{title}</h3>
    <p className="text-gray-600 text-sm">{children}</p>
  </div>
);

export default Scorecard;
